import { useState } from 'react';
import { ArrowLeft, Activity, Check, Sparkles, Zap, Crown, ArrowRight } from 'lucide-react';
import { motion } from 'framer-motion';

interface PricingPageProps {
  onBack: () => void;
  onGetStarted: () => void;
}

export default function PricingPage({ onBack, onGetStarted }: PricingPageProps) {
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'annual'>('annual');

  const plans = [
    {
      name: 'Monthly',
      price: 7,
      period: 'month',
      duration: 1,
      savings: null,
      icon: Zap,
      popular: false,
      features: [
        'AI-powered food scanning',
        'Biological age tracking',
        'Daily health metrics',
        'Nutrition analysis',
        'Basic activity tracking',
        'Email support'
      ]
    },
    {
      name: 'Annual',
      price: 60,
      period: 'year',
      duration: 12,
      savings: '29%',
      icon: Crown,
      popular: true,
      features: [
        'Everything in Monthly',
        'Advanced biomarker analysis',
        'Personalized protocols',
        'Future face prediction',
        'Priority support',
        'Early access to new features',
        'Export health data'
      ]
    },
    {
      name: 'Lifetime',
      price: 199,
      period: 'lifetime',
      duration: null,
      savings: 'Best Value',
      icon: Sparkles,
      popular: false,
      features: [
        'Everything in Annual',
        'Lifetime access',
        'All future updates',
        'VIP support',
        'Beta feature access',
        'Custom protocol design',
        'One-time payment'
      ]
    }
  ];

  const faqs = [
    {
      question: 'Can I cancel anytime?',
      answer: 'Yes, you can cancel your subscription at any time. Your access will continue until the end of your billing period.'
    },
    {
      question: 'Is there a free trial?',
      answer: 'We offer a 7-day free trial for first-time users. No credit card required to start.'
    },
    {
      question: 'What payment methods do you accept?',
      answer: 'We accept all major credit cards, debit cards, and PayPal.'
    },
    {
      question: 'Do you offer refunds?',
      answer: 'We offer a 30-day money-back guarantee. If you\'re not satisfied, contact us for a full refund.'
    },
    {
      question: 'Can I upgrade or downgrade my plan?',
      answer: 'Yes, you can change your plan at any time. Upgrades take effect immediately, while downgrades apply at the next billing cycle.'
    },
    {
      question: 'Is my payment information secure?',
      answer: 'Absolutely. We use industry-standard encryption and never store your payment details on our servers.'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-lg border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={onBack}
              className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="font-medium">Back</span>
            </button>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center">
                <Activity className="w-5 h-5 text-white" strokeWidth={2.5} />
              </div>
              <span className="font-bold text-gray-900">AgeQ AI</span>
            </div>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
        {/* Hero Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center mb-12"
        >
          <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
            Choose Your Plan
          </h1>
          <p className="text-lg sm:text-xl text-gray-600 max-w-2xl mx-auto">
            Start reversing your biological age today. Pick the plan that fits your health journey.
          </p>
        </motion.div>

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-3 gap-6 lg:gap-8 mb-16">
          {plans.map((plan, index) => {
            const Icon = plan.icon;
            return (
              <motion.div
                key={plan.name}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className={`relative rounded-2xl p-8 ${
                  plan.popular
                    ? 'bg-gradient-to-br from-cyan-500 to-blue-600 text-white shadow-2xl scale-105'
                    : 'bg-white border-2 border-gray-200 hover:border-cyan-300 hover:shadow-xl'
                } transition-all`}
              >
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 px-4 py-1 bg-amber-400 text-gray-900 text-sm font-bold rounded-full">
                    MOST POPULAR
                  </div>
                )}

                {plan.savings && !plan.popular && (
                  <div className="absolute -top-4 right-4 px-3 py-1 bg-green-500 text-white text-xs font-bold rounded-full">
                    {plan.savings} OFF
                  </div>
                )}

                <div className="flex items-center gap-3 mb-6">
                  <div className={`w-12 h-12 rounded-xl ${
                    plan.popular ? 'bg-white/20' : 'bg-cyan-50'
                  } flex items-center justify-center`}>
                    <Icon className={`w-6 h-6 ${plan.popular ? 'text-white' : 'text-cyan-600'}`} />
                  </div>
                  <div>
                    <h3 className={`text-2xl font-bold ${plan.popular ? 'text-white' : 'text-gray-900'}`}>
                      {plan.name}
                    </h3>
                  </div>
                </div>

                <div className="mb-6">
                  <div className="flex items-baseline gap-2">
                    <span className={`text-5xl font-bold ${plan.popular ? 'text-white' : 'text-gray-900'}`}>
                      ${plan.price}
                    </span>
                    <span className={`text-lg ${plan.popular ? 'text-white/80' : 'text-gray-600'}`}>
                      /{plan.period}
                    </span>
                  </div>
                  {plan.duration && plan.duration > 1 && (
                    <p className={`text-sm mt-2 ${plan.popular ? 'text-white/90' : 'text-gray-600'}`}>
                      ${(plan.price / plan.duration).toFixed(2)}/month
                    </p>
                  )}
                </div>

                <button
                  onClick={onGetStarted}
                  className={`w-full py-3 rounded-lg font-semibold transition-all mb-8 flex items-center justify-center gap-2 ${
                    plan.popular
                      ? 'bg-white text-cyan-600 hover:bg-gray-100'
                      : 'bg-gradient-to-r from-cyan-500 to-blue-600 text-white hover:from-cyan-600 hover:to-blue-700 shadow-lg hover:shadow-xl'
                  }`}
                >
                  Get Started
                  <ArrowRight className="w-4 h-4" />
                </button>

                <div className="space-y-3">
                  {plan.features.map((feature, idx) => (
                    <div key={idx} className="flex items-start gap-3">
                      <Check className={`w-5 h-5 flex-shrink-0 mt-0.5 ${
                        plan.popular ? 'text-white' : 'text-cyan-600'
                      }`} />
                      <span className={`text-sm ${plan.popular ? 'text-white/90' : 'text-gray-700'}`}>
                        {feature}
                      </span>
                    </div>
                  ))}
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Trust Indicators */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="bg-white rounded-2xl border border-gray-200 p-8 mb-16"
        >
          <div className="grid md:grid-cols-3 gap-8 text-center">
            <div>
              <div className="text-4xl font-bold text-cyan-600 mb-2">5M+</div>
              <p className="text-gray-600">Active Users</p>
            </div>
            <div>
              <div className="text-4xl font-bold text-cyan-600 mb-2">4.9/5</div>
              <p className="text-gray-600">Average Rating</p>
            </div>
            <div>
              <div className="text-4xl font-bold text-cyan-600 mb-2">30-Day</div>
              <p className="text-gray-600">Money-Back Guarantee</p>
            </div>
          </div>
        </motion.div>

        {/* FAQs */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.5 }}
          className="max-w-3xl mx-auto"
        >
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
            Frequently Asked Questions
          </h2>
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div
                key={index}
                className="bg-white rounded-xl border border-gray-200 p-6 hover:shadow-md transition-shadow"
              >
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {faq.question}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {faq.answer}
                </p>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Final CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.6 }}
          className="text-center mt-16 p-8 bg-gradient-to-r from-cyan-50 to-blue-50 rounded-2xl border border-cyan-200"
        >
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            Ready to Start Your Health Journey?
          </h2>
          <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
            Join millions of users who are already optimizing their health and reversing their biological age.
          </p>
          <button
            onClick={onGetStarted}
            className="px-8 py-4 bg-gradient-to-r from-cyan-500 to-blue-600 text-white font-semibold rounded-lg hover:from-cyan-600 hover:to-blue-700 transition-all shadow-lg hover:shadow-xl inline-flex items-center gap-2"
          >
            Get Started Now
            <ArrowRight className="w-5 h-5" />
          </button>
        </motion.div>
      </main>

      {/* Footer */}
      <footer className="border-t border-gray-200 mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
          <p className="text-center text-gray-500 text-sm">
            &copy; 2025 AgeQ AI. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}
